// JFrog Artifactory bulk-import admin page (v130).
//
// Two views in one:
//   - Top: form to start a new import + connection-test button.
//   - Below: list of past + running jobs with progress bars.
//
// Job-detail drilldown is inline as an expandable row showing per-
// repo status; we polled the list every 5s for live progress.

import { useState } from 'react';
import {
  Play, Square, Boxes, RotateCw, AlertTriangle, CheckCircle2, XCircle, Clock, ChevronRight, ChevronDown,
} from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardFooter, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import {
  useImports,
  useImport,
  useImportWorkers,
  startImport,
  cancelImport,
  retryFailed,
  testConnection,
  type ImportJob,
  type ImportStatus,
  type ImportSourceType,
  type ImportRepo,
  type GitHubPackageType,
} from '../../api/imports';

const SOURCE_LABELS: Record<ImportSourceType, string> = {
  jfrog: 'JFrog Artifactory',
  nexus: 'Sonatype Nexus 3',
  github: 'GitHub Packages',
  gitlab: 'GitLab Package Registry',
  azure: 'Azure DevOps Artifacts',
  awscodeartifact: 'AWS CodeArtifact',
  gcparhybrid: 'Google Artifact Registry',
};

const SOURCE_HELP: Record<ImportSourceType, { url: string; token: string }> = {
  jfrog: {
    url: 'https://your-jfrog.example.com',
    token: 'JFrog identity token',
  },
  nexus: {
    url: 'https://your-nexus.example.com',
    token: 'Nexus password (or "user:password")',
  },
  github: {
    url: 'https://api.github.com (leave empty for github.com; set for GHES)',
    token: 'PAT with read:packages (and repo if private)',
  },
  gitlab: {
    url: 'https://gitlab.com/api/v4 (leave empty for gitlab.com; set for self-hosted)',
    token: 'PAT or project-access-token with read_api scope',
  },
  azure: {
    url: 'https://dev.azure.com (leave empty for the public service; set for Azure DevOps Server)',
    token: 'PAT with vso.packaging scope',
  },
  awscodeartifact: {
    url: 'derived from AWS region — leave empty',
    token: 'keys:AKIA…:secret[:session]   or   bearer:<aws codeartifact get-authorization-token output>',
  },
  gcparhybrid: {
    url: 'fixed at artifactregistry.googleapis.com — leave empty',
    token: 'sa:<service-account JSON>   or   bearer:<oauth2 access token>',
  },
};

// Package_type filter options for the github source. Empty string =
// "all visible types". container is listed but isn't streamable in
// v1 — the importer will surface a per-repo error during the
// transfer phase if the operator picks it.
const GITHUB_PKG_TYPES: { value: GitHubPackageType; label: string }[] = [
  { value: '', label: 'All supported types (npm + maven + nuget + rubygems)' },
  { value: 'npm', label: 'npm only' },
  { value: 'maven', label: 'Maven only' },
  { value: 'nuget', label: 'NuGet only' },
  { value: 'rubygems', label: 'RubyGems only' },
  { value: 'container', label: 'Container (enumerate only — transfer not supported in v1)' },
];

function sourceBackendHelp(t: ImportSourceType): string {
  switch (t) {
    case 'jfrog':
      return 'Reads via JFrog REST API + AQL paging. Use an identity token from User Profile → Generate identity token.';
    case 'nexus':
      return 'Reads via Nexus 3 REST API + continuationToken paging. Use a Nexus password (or "user:password" form for non-default users).';
    case 'github':
      return 'Reads via GitHub REST v3 (api.github.com or GHES). Per-format download URLs (npm.pkg.github.com, nuget.pkg.github.com, …) authenticated with the same PAT. Container packages are enumerated but not streamed in v1 — use the OCI mirror flow for ghcr.io.';
    case 'gitlab':
      return 'Reads via GitLab REST v4 (gitlab.com or self-hosted). Scope by numeric project_id or group_id (mutually exclusive). Per-format download URLs (npm/maven/nuget/pypi/helm/generic/terraform) authenticated with the same PAT. composer / conan / debian / rpm enumerate only in v1; container is enumerable behind a toggle but transfer is deferred to the OCI mirror flow.';
    case 'azure':
      return 'Reads via Azure DevOps REST (api-version=7.1, dev.azure.com or Azure DevOps Server). Scope by org slug + optional project; an optional feed-name filter narrows the crawl to one feed. Per-protocol download URLs (npm/NuGet/Maven/PyPI/Universal) authenticated with the same PAT (HTTP basic, password-only).';
    case 'awscodeartifact':
      return 'Reads via AWS CodeArtifact REST API (codeartifact.<region>.amazonaws.com), AWS-SigV4-signed. Two auth modes: keys mode (AKIA…/secret with optional STS session token) signs every call locally; bearer mode accepts a pre-issued 12h token from `aws codeartifact get-authorization-token` and skips SigV4. Multi-format (npm / Maven / PyPI / NuGet / generic / swift / ruby) — every format enumerates as one Repository per (codeartifact_repo, format, package).';
    case 'gcparhybrid':
      return 'Reads via Google Cloud Artifact Registry REST API (artifactregistry.googleapis.com). Two auth modes: sa mode parses a service-account JSON key and runs the JWT-bearer assertion flow against oauth2.googleapis.com to mint a 1-hour access token (cached in-process); bearer mode accepts a pre-issued OAuth2 access token (e.g. from `gcloud auth print-access-token`) and uses it directly. Multi-format (npm / Maven / PyPI / Docker / Apt / Yum / generic / Go / KFP) — every format enumerates as one Repository per (artifact_registry_repo, format, package). Artifact Registry is regional, so one job scopes to one (project, location) tuple — operators with multi-region footprints run one job per location.';
  }
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  if (n < 1024 ** 4) return `${(n / 1024 ** 3).toFixed(2)} GB`;
  return `${(n / 1024 ** 4).toFixed(2)} TB`;
}

function fmtBps(n: number): string {
  if (n <= 0) return '—';
  return fmtBytes(n) + '/s';
}

function fmtDuration(seconds: number): string {
  if (!isFinite(seconds) || seconds <= 0) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${(seconds / 86400).toFixed(1)}d`;
}

function eta(bytesTotal: number, bytesDone: number, bps: number): string {
  if (bytesTotal <= 0 || bps <= 0) return '—';
  const remaining = bytesTotal - bytesDone;
  if (remaining <= 0) return 'done';
  return fmtDuration(remaining / bps);
}

function statusTone(s: ImportStatus): 'success' | 'warning' | 'danger' | 'neutral' | 'accent' {
  switch (s) {
    case 'completed': return 'success';
    case 'running': return 'accent';
    case 'pending': return 'warning';
    case 'failed': return 'danger';
    case 'cancelled': return 'neutral';
  }
}

function StatusIcon({ status }: Readonly<{ status: ImportStatus }>) {
  switch (status) {
    case 'completed': return <CheckCircle2 className="h-3 w-3" />;
    case 'failed': return <XCircle className="h-3 w-3" />;
    case 'running': return <RotateCw className="h-3 w-3 animate-spin" />;
    default: return <Clock className="h-3 w-3" />;
  }
}

function repoStatusTone(status: ImportRepo['status']): 'success' | 'danger' | 'accent' | 'neutral' {
  switch (status) {
    case 'completed': return 'success';
    case 'failed': return 'danger';
    case 'running': return 'accent';
    default: return 'neutral';
  }
}

function workerCardClassName(stale: boolean, currentArtifactID: number): string {
  if (stale) return 'border-warning/40 bg-warning/5';
  if (currentArtifactID > 0) return 'border-accent/30 bg-accent/5';
  return 'border-border bg-bg-inset/40';
}

// aws_region/aws_domain-derived token for the awscodeartifact source: the
// keys mode needs both access + secret key present, the bearer mode needs
// the bearer field.
function computeAwsToken(
  mode: 'keys' | 'bearer',
  accessKey: string,
  secretKey: string,
  sessionToken: string,
  bearerToken: string,
): string {
  if (mode === 'bearer') {
    return bearerToken ? `bearer:${bearerToken}` : '';
  }
  if (!accessKey || !secretKey) return '';
  const session = sessionToken ? ':' + sessionToken : '';
  return `keys:${accessKey}:${secretKey}${session}`;
}

// gcp_project_id-derived token for the gcparhybrid source: sa mode needs
// the service-account JSON, bearer mode needs the bearer field.
function computeGcpToken(mode: 'sa' | 'bearer', serviceAccountJSON: string, bearerToken: string): string {
  if (mode === 'bearer') {
    return bearerToken ? `bearer:${bearerToken}` : '';
  }
  return serviceAccountJSON ? `sa:${serviceAccountJSON}` : '';
}

// The two AWS/GCP source types compute api_token from their per-mode field
// set (see computeAwsToken/computeGcpToken above); every other source uses
// the plain api_token form field.
function resolveApiToken(
  sourceType: ImportSourceType,
  computedAwsToken: string,
  computedGcpToken: string,
  apiToken: string,
): string {
  if (sourceType === 'awscodeartifact') return computedAwsToken;
  if (sourceType === 'gcparhybrid') return computedGcpToken;
  return apiToken;
}

function GithubSourceFields({
  githubOrg,
  setGithubOrg,
  githubPackageType,
  setGithubPackageType,
}: Readonly<{
  githubOrg: string;
  setGithubOrg: (v: string) => void;
  githubPackageType: GitHubPackageType;
  setGithubPackageType: (v: GitHubPackageType) => void;
}>) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <label htmlFor="imp-github-org" className="block text-sm font-medium text-fg mb-1.5">
          GitHub organisation
        </label>
        <Input
          id="imp-github-org"
          value={githubOrg}
          onChange={(e) => setGithubOrg(e.target.value)}
          placeholder="e.g. acme-inc"
        />
        <p className="text-[10px] text-fg-subtle mt-1">
          We enumerate every <code>read:packages</code>-visible package
          belonging to this org. User-account packages aren't
          supported in v1.
        </p>
      </div>
      <div>
        <label htmlFor="imp-github-pkg-type" className="block text-sm font-medium text-fg mb-1.5">
          Package-type filter
        </label>
        <select
          id="imp-github-pkg-type"
          value={githubPackageType}
          onChange={(e) => setGithubPackageType(e.target.value as GitHubPackageType)}
          className="w-full h-10 rounded-md border border-border bg-bg-inset text-fg text-sm px-3"
        >
          {GITHUB_PKG_TYPES.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

function GitlabSourceFields({
  gitlabProjectID,
  setGitlabProjectID,
  gitlabGroupID,
  setGitlabGroupID,
  gitlabIncludeContainer,
  setGitlabIncludeContainer,
}: Readonly<{
  gitlabProjectID: string;
  setGitlabProjectID: (v: string) => void;
  gitlabGroupID: string;
  setGitlabGroupID: (v: string) => void;
  gitlabIncludeContainer: boolean;
  setGitlabIncludeContainer: (v: boolean) => void;
}>) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label htmlFor="imp-gitlab-project-id" className="block text-sm font-medium text-fg mb-1.5">
            GitLab project ID (numeric)
          </label>
          <Input
            id="imp-gitlab-project-id"
            value={gitlabProjectID}
            onChange={(e) => {
              setGitlabProjectID(e.target.value);
              if (e.target.value) setGitlabGroupID('');
            }}
            placeholder="e.g. 12345"
            disabled={!!gitlabGroupID}
          />
          <p className="text-[10px] text-fg-subtle mt-1">
            Find under <em>Project → Settings → General</em>. Mutually
            exclusive with group ID.
          </p>
        </div>
        <div>
          <label htmlFor="imp-gitlab-group-id" className="block text-sm font-medium text-fg mb-1.5">
            GitLab group ID (numeric)
          </label>
          <Input
            id="imp-gitlab-group-id"
            value={gitlabGroupID}
            onChange={(e) => {
              setGitlabGroupID(e.target.value);
              if (e.target.value) setGitlabProjectID('');
            }}
            placeholder="e.g. 9876"
            disabled={!!gitlabProjectID}
          />
          <p className="text-[10px] text-fg-subtle mt-1">
            Enumerates packages across every project in the group.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <input
          id="imp-gl-container"
          type="checkbox"
          checked={gitlabIncludeContainer}
          onChange={(e) => setGitlabIncludeContainer(e.target.checked)}
          disabled={!gitlabProjectID}
          className="h-4 w-4"
        />
        <label htmlFor="imp-gl-container" className="text-sm text-fg cursor-pointer">
          Also enumerate the project container registry{' '}
          <span className="text-fg-subtle ml-1">
            (project-scope only — v1 enumerates but does not stream layers; use the OCI mirror flow for transfer)
          </span>
        </label>
      </div>
    </div>
  );
}

function AzureSourceFields({
  azureOrganization,
  setAzureOrganization,
  azureProject,
  setAzureProject,
  azureFeed,
  setAzureFeed,
}: Readonly<{
  azureOrganization: string;
  setAzureOrganization: (v: string) => void;
  azureProject: string;
  setAzureProject: (v: string) => void;
  azureFeed: string;
  setAzureFeed: (v: string) => void;
}>) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div>
        <label htmlFor="imp-azure-org" className="block text-sm font-medium text-fg mb-1.5">
          Azure DevOps organisation
        </label>
        <Input
          id="imp-azure-org"
          value={azureOrganization}
          onChange={(e) => setAzureOrganization(e.target.value)}
          placeholder="e.g. acme-inc"
        />
        <p className="text-[10px] text-fg-subtle mt-1">
          The org slug from <code>dev.azure.com/&lt;org&gt;</code>.
        </p>
      </div>
      <div>
        <label htmlFor="imp-azure-project" className="block text-sm font-medium text-fg mb-1.5">
          Project (optional)
        </label>
        <Input
          id="imp-azure-project"
          value={azureProject}
          onChange={(e) => setAzureProject(e.target.value)}
          placeholder="empty = org-level feeds"
        />
        <p className="text-[10px] text-fg-subtle mt-1">
          Empty crawls org-scoped feeds; non-empty restricts to one project's feeds.
        </p>
      </div>
      <div>
        <label htmlFor="imp-azure-feed" className="block text-sm font-medium text-fg mb-1.5">
          Feed name filter (optional)
        </label>
        <Input
          id="imp-azure-feed"
          value={azureFeed}
          onChange={(e) => setAzureFeed(e.target.value)}
          placeholder="e.g. internal-npm"
        />
        <p className="text-[10px] text-fg-subtle mt-1">
          Empty enumerates every feed visible to the PAT (case-insensitive exact match on name or UUID).
        </p>
      </div>
    </div>
  );
}

function AwsSourceFields({
  awsRegion,
  setAwsRegion,
  awsDomain,
  setAwsDomain,
  awsDomainOwner,
  setAwsDomainOwner,
  awsAuthMode,
  setAwsAuthMode,
  awsAccessKey,
  setAwsAccessKey,
  awsSecretKey,
  setAwsSecretKey,
  awsSessionToken,
  setAwsSessionToken,
  awsBearerToken,
  setAwsBearerToken,
}: Readonly<{
  awsRegion: string;
  setAwsRegion: (v: string) => void;
  awsDomain: string;
  setAwsDomain: (v: string) => void;
  awsDomainOwner: string;
  setAwsDomainOwner: (v: string) => void;
  awsAuthMode: 'keys' | 'bearer';
  setAwsAuthMode: (v: 'keys' | 'bearer') => void;
  awsAccessKey: string;
  setAwsAccessKey: (v: string) => void;
  awsSecretKey: string;
  setAwsSecretKey: (v: string) => void;
  awsSessionToken: string;
  setAwsSessionToken: (v: string) => void;
  awsBearerToken: string;
  setAwsBearerToken: (v: string) => void;
}>) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label htmlFor="imp-aws-region" className="block text-sm font-medium text-fg mb-1.5">
            AWS region
          </label>
          <Input
            id="imp-aws-region"
            value={awsRegion}
            onChange={(e) => setAwsRegion(e.target.value)}
            placeholder="e.g. us-east-1"
          />
          <p className="text-[10px] text-fg-subtle mt-1">
            The region your CodeArtifact domain lives in. Used both
            for the SigV4 signing scope and to derive the API host
            (<code>codeartifact.&lt;region&gt;.amazonaws.com</code>).
          </p>
        </div>
        <div>
          <label htmlFor="imp-aws-domain" className="block text-sm font-medium text-fg mb-1.5">
            CodeArtifact domain
          </label>
          <Input
            id="imp-aws-domain"
            value={awsDomain}
            onChange={(e) => setAwsDomain(e.target.value)}
            placeholder="e.g. acme-platform"
          />
          <p className="text-[10px] text-fg-subtle mt-1">
            The per-account container holding your repositories.
          </p>
        </div>
        <div>
          <label htmlFor="imp-aws-domain-owner" className="block text-sm font-medium text-fg mb-1.5">
            Domain owner (optional)
          </label>
          <Input
            id="imp-aws-domain-owner"
            value={awsDomainOwner}
            onChange={(e) => setAwsDomainOwner(e.target.value)}
            placeholder="12-digit AWS account ID"
          />
          <p className="text-[10px] text-fg-subtle mt-1">
            Empty = the credentials' own account; non-empty for cross-account imports.
          </p>
        </div>
      </div>
      <div>
        <label id="imp-aws-auth-mode-label" className="block text-sm font-medium text-fg mb-1.5">
          Authentication mode
        </label>
        <div className="flex gap-2" role="group" aria-labelledby="imp-aws-auth-mode-label">
          {(['keys', 'bearer'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setAwsAuthMode(m)}
              className={
                'inline-flex items-center gap-2 h-9 px-3 rounded-md text-sm border transition-colors ' +
                (awsAuthMode === m
                  ? 'border-nebula text-nebula bg-nebula/10 font-medium'
                  : 'border-border text-fg-muted hover:text-fg hover:border-border-strong')
              }
            >
              {m === 'keys' ? 'Long-lived keys / STS' : 'Pre-issued bearer'}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-fg-subtle mt-1">
          {awsAuthMode === 'keys'
            ? 'OrbitalReg signs every CodeArtifact call locally with AWS SigV4. For STS-assumed roles, paste the temporary access_key + secret + session_token from `aws sts assume-role`.'
            : 'Paste the output of `aws codeartifact get-authorization-token --domain <domain> --query authorizationToken --output text`. Valid for up to 12 hours; the import re-runs need a fresh token.'}
        </p>
      </div>
      {awsAuthMode === 'keys' ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label htmlFor="imp-aws-access-key" className="block text-sm font-medium text-fg mb-1.5">
              Access key ID
            </label>
            <Input
              id="imp-aws-access-key"
              value={awsAccessKey}
              onChange={(e) => setAwsAccessKey(e.target.value)}
              placeholder="AKIA… or ASIA…"
            />
          </div>
          <div>
            <label htmlFor="imp-aws-secret-key" className="block text-sm font-medium text-fg mb-1.5">
              Secret access key
            </label>
            <Input
              id="imp-aws-secret-key"
              type="password"
              value={awsSecretKey}
              onChange={(e) => setAwsSecretKey(e.target.value)}
              placeholder="40-char base64-ish secret"
            />
          </div>
          <div>
            <label htmlFor="imp-aws-session-token" className="block text-sm font-medium text-fg mb-1.5">
              Session token (STS only)
            </label>
            <Input
              id="imp-aws-session-token"
              type="password"
              value={awsSessionToken}
              onChange={(e) => setAwsSessionToken(e.target.value)}
              placeholder="empty for long-lived keys"
            />
          </div>
        </div>
      ) : (
        <div>
          <label htmlFor="imp-aws-bearer-token" className="block text-sm font-medium text-fg mb-1.5">
            Authorization token
          </label>
          <Input
            id="imp-aws-bearer-token"
            type="password"
            value={awsBearerToken}
            onChange={(e) => setAwsBearerToken(e.target.value)}
            placeholder="eyJ… (12h-valid CodeArtifact bearer)"
          />
        </div>
      )}
    </div>
  );
}

function GcpSourceFields({
  gcpProjectID,
  setGcpProjectID,
  gcpLocation,
  setGcpLocation,
  gcpRepository,
  setGcpRepository,
  gcpAuthMode,
  setGcpAuthMode,
  gcpServiceAccountJson,
  setGcpServiceAccountJson,
  gcpBearerToken,
  setGcpBearerToken,
}: Readonly<{
  gcpProjectID: string;
  setGcpProjectID: (v: string) => void;
  gcpLocation: string;
  setGcpLocation: (v: string) => void;
  gcpRepository: string;
  setGcpRepository: (v: string) => void;
  gcpAuthMode: 'sa' | 'bearer';
  setGcpAuthMode: (v: 'sa' | 'bearer') => void;
  gcpServiceAccountJson: string;
  setGcpServiceAccountJson: (v: string) => void;
  gcpBearerToken: string;
  setGcpBearerToken: (v: string) => void;
}>) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label htmlFor="imp-gcp-project-id" className="block text-sm font-medium text-fg mb-1.5">
            GCP project ID
          </label>
          <Input
            id="imp-gcp-project-id"
            value={gcpProjectID}
            onChange={(e) => setGcpProjectID(e.target.value)}
            placeholder="e.g. acme-platform-prod"
          />
          <p className="text-[10px] text-fg-subtle mt-1">
            The project that owns the Artifact Registry repositories.
            Find under <em>Google Cloud Console → Project info</em>.
          </p>
        </div>
        <div>
          <label htmlFor="imp-gcp-location" className="block text-sm font-medium text-fg mb-1.5">
            Location
          </label>
          <Input
            id="imp-gcp-location"
            value={gcpLocation}
            onChange={(e) => setGcpLocation(e.target.value)}
            placeholder="e.g. us-east1"
          />
          <p className="text-[10px] text-fg-subtle mt-1">
            Region (<code>us-east1</code>), multi-region (<code>us</code>),
            or zone. One job per (project, location).
          </p>
        </div>
        <div>
          <label htmlFor="imp-gcp-repository" className="block text-sm font-medium text-fg mb-1.5">
            Repository filter (optional)
          </label>
          <Input
            id="imp-gcp-repository"
            value={gcpRepository}
            onChange={(e) => setGcpRepository(e.target.value)}
            placeholder="empty = every repo"
          />
          <p className="text-[10px] text-fg-subtle mt-1">
            Restrict the crawl to one named repository (case-sensitive
            exact match).
          </p>
        </div>
      </div>
      <div>
        <label id="imp-gcp-auth-mode-label" className="block text-sm font-medium text-fg mb-1.5">
          Authentication mode
        </label>
        <div className="flex gap-2" role="group" aria-labelledby="imp-gcp-auth-mode-label">
          {(['sa', 'bearer'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setGcpAuthMode(m)}
              className={
                'inline-flex items-center gap-2 h-9 px-3 rounded-md text-sm border transition-colors ' +
                (gcpAuthMode === m
                  ? 'border-nebula text-nebula bg-nebula/10 font-medium'
                  : 'border-border text-fg-muted hover:text-fg hover:border-border-strong')
              }
            >
              {m === 'sa' ? 'Service-account JSON key' : 'Pre-issued OAuth bearer'}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-fg-subtle mt-1">
          {gcpAuthMode === 'sa'
            ? 'Paste the JSON key from `gcloud iam service-accounts keys create`. OrbitalReg signs a JWT with the embedded RSA key, exchanges it for a 1-hour OAuth token, and refreshes transparently for long-running imports.'
            : 'Paste the output of `gcloud auth print-access-token`. Valid for ~60 minutes; subsequent re-runs of this import need a fresh token.'}
        </p>
      </div>
      {gcpAuthMode === 'sa' ? (
        <div>
          <label htmlFor="imp-gcp-sa-json" className="block text-sm font-medium text-fg mb-1.5">
            Service-account JSON key
          </label>
          <textarea
            id="imp-gcp-sa-json"
            value={gcpServiceAccountJson}
            onChange={(e) => setGcpServiceAccountJson(e.target.value)}
            placeholder='{"type":"service_account","project_id":"…","private_key":"-----BEGIN PRIVATE KEY-----\n…","client_email":"…@….iam.gserviceaccount.com",…}'
            className="w-full h-32 rounded-md border border-border bg-bg-inset text-fg text-xs font-mono px-3 py-2"
          />
          <p className="text-[10px] text-fg-subtle mt-1">
            Required IAM roles: <code>roles/artifactregistry.reader</code> on
            each repository (or <code>roles/artifactregistry.reader</code> at
            project level for the easy path). Never written to disk —
            we use it once to mint the access token, then drop it.
          </p>
        </div>
      ) : (
        <div>
          <label htmlFor="imp-gcp-oauth-token" className="block text-sm font-medium text-fg mb-1.5">
            OAuth2 access token
          </label>
          <Input
            id="imp-gcp-oauth-token"
            type="password"
            value={gcpBearerToken}
            onChange={(e) => setGcpBearerToken(e.target.value)}
            placeholder="ya29.… (≤1h-valid GCP access token)"
          />
        </div>
      )}
    </div>
  );
}

// Backward-compat wrapper — see AdminImportsHub for the consolidated
// IA. App.tsx redirects /admin/imports to /admin/imports?tab=jfrog
// (no-op default); this export remains for any direct importers.
export function AdminImports() {
  return (
    <AdminShell
      title="External imports"
      description="Bulk-migrate repositories and artifacts from another registry into OrbitalReg. Pick the source backend, paste credentials, and the import runs in a background goroutine — you can leave this page open or come back later. Idempotent on (repo, path, version): re-running picks up where it left off."
    >
      <AdminImportsBody />
    </AdminShell>
  );
}

// Body for the Imports hub — no <AdminShell> wrapper. When
// `lockSource` is set, the source-backend toggle is hidden, the form
// is forced to that backend, and the jobs list is filtered to it via
// the backend `source_type=` query param. Used by the per-backend
// tabs in AdminImportsHub (JFrog tab → 'jfrog', Nexus tab → 'nexus').
// When undefined, all jobs are shown and the toggle is rendered.
export function AdminImportsBody({ lockSource }: Readonly<{ lockSource?: ImportSourceType }> = {}) {
  const { data: jobs, mutate } = useImports(lockSource);
  const [name, setName] = useState('');
  const [sourceType, setSourceType] = useState<ImportSourceType>(lockSource ?? 'jfrog');
  const [sourceURL, setSourceURL] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [targetProject, setTargetProject] = useState('');
  const [repoFilter, setRepoFilter] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [workerCount, setWorkerCount] = useState(8);
  const [maxRetries, setMaxRetries] = useState(5);
  const [githubOrg, setGithubOrg] = useState('');
  const [githubPackageType, setGithubPackageType] = useState<GitHubPackageType>('');
  const [gitlabProjectID, setGitlabProjectID] = useState('');
  const [gitlabGroupID, setGitlabGroupID] = useState('');
  const [gitlabIncludeContainer, setGitlabIncludeContainer] = useState(false);
  const [azureOrganization, setAzureOrganization] = useState('');
  const [azureProject, setAzureProject] = useState('');
  const [azureFeed, setAzureFeed] = useState('');
  const [awsRegion, setAwsRegion] = useState('us-east-1');
  const [awsDomain, setAwsDomain] = useState('');
  const [awsDomainOwner, setAwsDomainOwner] = useState('');
  const [awsAuthMode, setAwsAuthMode] = useState<'keys' | 'bearer'>('keys');
  const [awsAccessKey, setAwsAccessKey] = useState('');
  const [awsSecretKey, setAwsSecretKey] = useState('');
  const [awsSessionToken, setAwsSessionToken] = useState('');
  const [awsBearerToken, setAwsBearerToken] = useState('');
  const [gcpProjectID, setGcpProjectID] = useState('');
  const [gcpLocation, setGcpLocation] = useState('us-east1');
  const [gcpRepository, setGcpRepository] = useState('');
  const [gcpAuthMode, setGcpAuthMode] = useState<'sa' | 'bearer'>('sa');
  const [gcpServiceAccountJson, setGcpServiceAccountJson] = useState('');
  const [gcpBearerToken, setGcpBearerToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [test, setTest] = useState<{ ok: boolean; msg: string } | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  // For github the source_url is optional (defaults to api.github.com)
  // and the org takes its place as the mandatory identifier. For
  // gitlab the source_url is optional (defaults to gitlab.com/api/v4)
  // and exactly one of project_id / group_id is the mandatory scope.
  // For azure the source_url is optional (defaults to dev.azure.com)
  // and the organization takes its place as the mandatory identifier.
  // For awscodeartifact the source_url is unused (derived from region)
  // and aws_region + aws_domain are the mandatory identifiers; the
  // api_token is computed from awsAuthMode + the per-mode field set.
  const computedAwsToken = computeAwsToken(awsAuthMode, awsAccessKey, awsSecretKey, awsSessionToken, awsBearerToken);
  const computedGcpToken = computeGcpToken(gcpAuthMode, gcpServiceAccountJson, gcpBearerToken);
  let canTest: boolean;
  if (sourceType === 'github') {
    canTest = !!apiToken && !!githubOrg;
  } else if (sourceType === 'gitlab') {
    const oneOf = !!gitlabProjectID !== !!gitlabGroupID;
    canTest = !!apiToken && oneOf;
  } else if (sourceType === 'azure') {
    canTest = !!apiToken && !!azureOrganization;
  } else if (sourceType === 'awscodeartifact') {
    canTest = !!awsRegion && !!awsDomain && !!computedAwsToken;
  } else if (sourceType === 'gcparhybrid') {
    canTest = !!gcpProjectID && !!gcpLocation && !!computedGcpToken;
  } else {
    canTest = !!sourceURL && !!apiToken;
  }
  const canStart = canTest && !!targetProject;

  const onTest = async () => {
    if (!canTest) return;
    setBusy(true); setErr(null); setTest(null);
    try {
      const r = await testConnection({
        source_type: sourceType,
        source_url: sourceURL,
        api_token: resolveApiToken(sourceType, computedAwsToken, computedGcpToken, apiToken),
        github_org: sourceType === 'github' ? githubOrg : undefined,
        github_package_type: sourceType === 'github' ? githubPackageType : undefined,
        gitlab_project_id: sourceType === 'gitlab' ? gitlabProjectID : undefined,
        gitlab_group_id: sourceType === 'gitlab' ? gitlabGroupID : undefined,
        gitlab_include_container: sourceType === 'gitlab' ? gitlabIncludeContainer : undefined,
        azure_organization: sourceType === 'azure' ? azureOrganization : undefined,
        azure_project: sourceType === 'azure' ? azureProject : undefined,
        azure_feed: sourceType === 'azure' ? azureFeed : undefined,
        aws_region: sourceType === 'awscodeartifact' ? awsRegion : undefined,
        aws_domain: sourceType === 'awscodeartifact' ? awsDomain : undefined,
        aws_domain_owner: sourceType === 'awscodeartifact' ? awsDomainOwner : undefined,
        gcp_project_id: sourceType === 'gcparhybrid' ? gcpProjectID : undefined,
        gcp_location: sourceType === 'gcparhybrid' ? gcpLocation : undefined,
        gcp_repository: sourceType === 'gcparhybrid' ? gcpRepository : undefined,
      });
      if (r.ok) {
        setTest({ ok: true, msg: `Connected to ${SOURCE_LABELS[sourceType]}. ${r.repos_visible ?? '?'} repositories visible.${r.warning ? ' (' + r.warning + ')' : ''}` });
      } else {
        setTest({ ok: false, msg: r.error ?? 'Unknown error' });
      }
    } catch (e) {
      setTest({ ok: false, msg: String(e) });
    } finally {
      setBusy(false);
    }
  };

  const onStart = async () => {
    if (!canStart) return;
    setBusy(true); setErr(null);
    try {
      await startImport({
        name: name || undefined,
        source_type: sourceType,
        source_url: sourceURL,
        api_token: resolveApiToken(sourceType, computedAwsToken, computedGcpToken, apiToken),
        target_project_name: targetProject,
        repo_filter: repoFilter || undefined,
        dry_run: dryRun,
        worker_count: workerCount,
        max_retries: maxRetries,
        github_org: sourceType === 'github' ? githubOrg : undefined,
        github_package_type: sourceType === 'github' ? githubPackageType : undefined,
        gitlab_project_id: sourceType === 'gitlab' ? gitlabProjectID : undefined,
        gitlab_group_id: sourceType === 'gitlab' ? gitlabGroupID : undefined,
        gitlab_include_container: sourceType === 'gitlab' ? gitlabIncludeContainer : undefined,
        azure_organization: sourceType === 'azure' ? azureOrganization : undefined,
        azure_project: sourceType === 'azure' ? azureProject : undefined,
        azure_feed: sourceType === 'azure' ? azureFeed : undefined,
        aws_region: sourceType === 'awscodeartifact' ? awsRegion : undefined,
        aws_domain: sourceType === 'awscodeartifact' ? awsDomain : undefined,
        aws_domain_owner: sourceType === 'awscodeartifact' ? awsDomainOwner : undefined,
        gcp_project_id: sourceType === 'gcparhybrid' ? gcpProjectID : undefined,
        gcp_location: sourceType === 'gcparhybrid' ? gcpLocation : undefined,
        gcp_repository: sourceType === 'gcparhybrid' ? gcpRepository : undefined,
      });
      // Wipe the form (especially the token + AWS / GCP secrets).
      setName(''); setApiToken('');
      setAwsAccessKey(''); setAwsSecretKey(''); setAwsSessionToken(''); setAwsBearerToken('');
      setGcpServiceAccountJson(''); setGcpBearerToken('');
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onCancel = async (id: string) => {
    if (!confirm('Cancel this import? Already-transferred artifacts stay in place.')) return;
    try {
      await cancelImport(id);
    } catch (e) {
      alert(String(e));
    }
    await mutate();
  };

  const onRetry = async (id: string) => {
    if (!confirm('Retry every failed artifact in this job? Their attempts counter is reset to 0.')) return;
    try {
      await retryFailed(id);
    } catch (e) {
      alert(String(e));
    }
    await mutate();
  };

  return (
    <>
      <Card>
        <CardHeader
          title={lockSource ? `Start a new ${SOURCE_LABELS[lockSource]} import` : 'Start a new import'}
          description="Test the connection, then run a dry-run before the real transfer."
        />
        <CardBody className="space-y-4">
          {!lockSource && (
            <div>
              <label id="imp-source-backend-label" className="block text-sm font-medium text-fg mb-1.5">
                Source backend
              </label>
              <div className="flex gap-2 flex-wrap" role="group" aria-labelledby="imp-source-backend-label">
                {(['jfrog', 'nexus', 'github', 'gitlab', 'azure', 'awscodeartifact', 'gcparhybrid'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setSourceType(t)}
                    className={
                      'inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm border transition-colors ' +
                      (sourceType === t
                        ? 'border-nebula text-nebula bg-nebula/10 font-medium'
                        : 'border-border text-fg-muted hover:text-fg hover:border-border-strong')
                    }
                  >
                    {SOURCE_LABELS[t]}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-fg-subtle mt-1.5">
                {sourceBackendHelp(sourceType)}
              </p>
            </div>
          )}
          {lockSource && (
            <p className="text-[11px] text-fg-subtle">
              {sourceBackendHelp(lockSource)}
            </p>
          )}
          {sourceType === 'github' && (
            <GithubSourceFields
              githubOrg={githubOrg}
              setGithubOrg={setGithubOrg}
              githubPackageType={githubPackageType}
              setGithubPackageType={setGithubPackageType}
            />
          )}
          {sourceType === 'gitlab' && (
            <GitlabSourceFields
              gitlabProjectID={gitlabProjectID}
              setGitlabProjectID={setGitlabProjectID}
              gitlabGroupID={gitlabGroupID}
              setGitlabGroupID={setGitlabGroupID}
              gitlabIncludeContainer={gitlabIncludeContainer}
              setGitlabIncludeContainer={setGitlabIncludeContainer}
            />
          )}
          {sourceType === 'azure' && (
            <AzureSourceFields
              azureOrganization={azureOrganization}
              setAzureOrganization={setAzureOrganization}
              azureProject={azureProject}
              setAzureProject={setAzureProject}
              azureFeed={azureFeed}
              setAzureFeed={setAzureFeed}
            />
          )}
          {sourceType === 'awscodeartifact' && (
            <AwsSourceFields
              awsRegion={awsRegion}
              setAwsRegion={setAwsRegion}
              awsDomain={awsDomain}
              setAwsDomain={setAwsDomain}
              awsDomainOwner={awsDomainOwner}
              setAwsDomainOwner={setAwsDomainOwner}
              awsAuthMode={awsAuthMode}
              setAwsAuthMode={setAwsAuthMode}
              awsAccessKey={awsAccessKey}
              setAwsAccessKey={setAwsAccessKey}
              awsSecretKey={awsSecretKey}
              setAwsSecretKey={setAwsSecretKey}
              awsSessionToken={awsSessionToken}
              setAwsSessionToken={setAwsSessionToken}
              awsBearerToken={awsBearerToken}
              setAwsBearerToken={setAwsBearerToken}
            />
          )}
          {sourceType === 'gcparhybrid' && (
            <GcpSourceFields
              gcpProjectID={gcpProjectID}
              setGcpProjectID={setGcpProjectID}
              gcpLocation={gcpLocation}
              setGcpLocation={setGcpLocation}
              gcpRepository={gcpRepository}
              setGcpRepository={setGcpRepository}
              gcpAuthMode={gcpAuthMode}
              setGcpAuthMode={setGcpAuthMode}
              gcpServiceAccountJson={gcpServiceAccountJson}
              setGcpServiceAccountJson={setGcpServiceAccountJson}
              gcpBearerToken={gcpBearerToken}
              setGcpBearerToken={setGcpBearerToken}
            />
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="job name (optional)" />
            <Input value={targetProject} onChange={(e) => setTargetProject(e.target.value)} placeholder="target project (slug or display name)" />
          </div>
          {sourceType !== 'awscodeartifact' && sourceType !== 'gcparhybrid' && (
            <Input
              value={sourceURL}
              onChange={(e) => setSourceURL(e.target.value)}
              placeholder={SOURCE_HELP[sourceType].url}
            />
          )}
          {sourceType !== 'awscodeartifact' && sourceType !== 'gcparhybrid' && (
            <Input
              type="password"
              value={apiToken}
              onChange={(e) => setApiToken(e.target.value)}
              placeholder={SOURCE_HELP[sourceType].token}
            />
          )}
          <Input
            value={repoFilter}
            onChange={(e) => setRepoFilter(e.target.value)}
            placeholder='repo filter glob (e.g. "maven-*", empty = all)'
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="imp-worker-count" className="block text-sm font-medium text-fg mb-1.5">
                Parallel workers
              </label>
              <Input
                id="imp-worker-count"
                type="number"
                value={workerCount}
                onChange={(e) => setWorkerCount(Number.parseInt(e.target.value, 10) || 8)}
                min={1}
                max={128}
              />
              <p className="text-[10px] text-fg-subtle mt-1">
                For multi-TB transfers start with 16–32 and watch the throughput
                + 429 rate from the source. Each worker holds one HTTP stream and
                one S3 multipart in flight.
              </p>
            </div>
            <div>
              <label htmlFor="imp-max-retries" className="block text-sm font-medium text-fg mb-1.5">
                Max retries per artifact
              </label>
              <Input
                id="imp-max-retries"
                type="number"
                value={maxRetries}
                onChange={(e) => setMaxRetries(Number.parseInt(e.target.value, 10) || 5)}
                min={0}
                max={20}
              />
              <p className="text-[10px] text-fg-subtle mt-1">
                Exponential backoff (30s / 2m / 8m / 30m / 2h). After this many failures the row is parked as 'failed' and can be retried via the button below.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input
              id="imp-dry"
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              className="h-4 w-4"
            />
            <label htmlFor="imp-dry" className="text-sm text-fg cursor-pointer">
              Dry-run (list what would be transferred without writing anything)
            </label>
          </div>

          {test && (
            <div
              className={
                'text-sm flex items-center gap-2 rounded-md p-3 ' +
                (test.ok
                  ? 'bg-success/5 border border-success/40 text-fg'
                  : 'bg-danger/5 border border-danger/40 text-danger font-mono')
              }
            >
              {test.ok ? <CheckCircle2 className="h-4 w-4 text-success" /> : <AlertTriangle className="h-4 w-4" />}
              {test.msg}
            </div>
          )}
          {err && <p className="text-danger font-mono text-sm">{err}</p>}
        </CardBody>
        <CardFooter className="flex justify-end gap-2">
          <Button variant="secondary" disabled={busy || !canTest} onClick={onTest}>
            Test connection
          </Button>
          <Button
            variant="primary"
            disabled={busy || !canStart}
            onClick={onStart}
          >
            <Play className="h-4 w-4" />
            {dryRun ? 'Run dry-run' : 'Start import'}
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader
          title={`Imports (${jobs?.length ?? 0})`}
          description="Auto-refreshed every 5 seconds. Click a row to expand and see per-repo progress."
          actions={
            <button
              type="button"
              onClick={() => mutate()}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-sm border border-border hover:bg-bg-hover text-fg-muted hover:text-fg"
            >
              <RotateCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted w-8" />
                <th className="px-5 py-3 font-medium text-fg-muted">Status / phase</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Name</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Repos</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Bytes</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Throughput</th>
                <th className="px-5 py-3 font-medium text-fg-muted">ETA</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Dedup</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {jobs?.length ? (
                jobs.map((j) => (
                  <ImportRow
                    key={j.id}
                    job={j}
                    expanded={expanded === j.id}
                    onToggle={() => setExpanded(expanded === j.id ? null : j.id)}
                    onCancel={() => onCancel(j.id)}
                    onRetry={() => onRetry(j.id)}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="px-5 py-12 text-center text-fg-muted text-sm">
                    <Boxes className="h-8 w-8 mx-auto mb-2 text-fg-subtle" />
                    No imports yet — start one above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

function ImportRow({
  job,
  expanded,
  onToggle,
  onCancel,
  onRetry,
}: Readonly<{
  job: ImportJob;
  expanded: boolean;
  onToggle: () => void;
  onCancel: () => void;
  onRetry: () => void;
}>) {
  const { data: detail } = useImport(expanded ? job.id : undefined);
  const repoPct = job.repos_total > 0 ? (job.repos_done / job.repos_total) * 100 : 0;
  const bytesPct = job.bytes_total > 0 ? (job.bytes_done / job.bytes_total) * 100 : 0;
  const dedupPct = job.bytes_total > 0 ? (job.dedup_bytes / job.bytes_total) * 100 : 0;

  return (
    <>
      <tr className="border-b border-border/60 hover:bg-bg-hover/40">
        <td className="px-2 py-3 text-center">
          <button type="button" onClick={onToggle} className="text-fg-muted hover:text-fg">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </td>
        <td className="px-5 py-3">
          <Badge tone={statusTone(job.status)}>
            <StatusIcon status={job.status} />
            {job.status}
          </Badge>
          <p className="text-[10px] text-fg-subtle mt-1">
            phase: <span className="orb-code">{job.phase}</span>
            {job.dry_run && <span className="ml-2">dry-run</span>}
          </p>
        </td>
        <td className="px-5 py-3">
          <p className="text-fg">{job.name}</p>
          <p className="text-[10px] text-fg-subtle">
            {SOURCE_LABELS[job.source_type] ?? job.source_type} · {job.worker_count} workers
          </p>
          <p className="text-[10px] text-fg-subtle font-mono break-all max-w-[220px]">{job.source_url}</p>
        </td>
        <td className="px-5 py-3 text-xs">
          {job.repos_done}/{job.repos_total}
          <div className="h-1 mt-1 w-20 bg-bg-inset rounded">
            <div className="h-1 rounded bg-accent" style={{ width: repoPct + '%' }} />
          </div>
          <p className="text-[10px] text-fg-subtle mt-0.5">{job.artifacts_done.toLocaleString()}/{job.artifacts_total.toLocaleString()} artifacts</p>
        </td>
        <td className="px-5 py-3 text-xs">
          {fmtBytes(job.bytes_done)} / {fmtBytes(job.bytes_total)}
          <div className="h-1 mt-1 w-24 bg-bg-inset rounded relative">
            <div className="h-1 rounded bg-success absolute left-0 top-0" style={{ width: dedupPct + '%' }} />
            <div className="h-1 rounded bg-nebula absolute top-0" style={{ left: dedupPct + '%', width: (bytesPct - dedupPct) + '%' }} />
          </div>
          <p className="text-[10px] text-fg-subtle mt-0.5">{bytesPct.toFixed(1)}%</p>
        </td>
        <td className="px-5 py-3 text-xs">
          <span className={job.throughput_bps > 0 ? 'text-fg' : 'text-fg-subtle'}>
            {fmtBps(job.throughput_bps)}
          </span>
          {job.effective_workers > 0 && job.effective_workers < job.worker_count && (
            <p className="text-[10px] text-warning mt-0.5" title="JFrog rate-limit detected — workers throttled">
              ⚠ {job.effective_workers}/{job.worker_count} workers
            </p>
          )}
        </td>
        <td className="px-5 py-3 text-xs text-fg">
          {eta(job.bytes_total, job.bytes_done, job.throughput_bps)}
        </td>
        <td className="px-5 py-3 text-xs">
          <p className="text-success">{job.dedup_hits.toLocaleString()} hits</p>
          <p className="text-fg-subtle text-[10px]">saved {fmtBytes(job.dedup_bytes)}</p>
        </td>
        <td className="px-5 py-3 text-right">
          <div className="flex items-center justify-end gap-1">
            {(job.status === 'running' || job.status === 'pending') && !job.cancel_requested && (
              <button
                type="button"
                onClick={onCancel}
                title="Cancel"
                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-danger/10 hover:text-danger"
              >
                <Square className="h-4 w-4" />
              </button>
            )}
            {job.status === 'running' && (
              <button
                type="button"
                onClick={onRetry}
                title="Retry failed artifacts"
                className="h-8 px-2 inline-flex items-center gap-1 rounded-md border border-border text-fg-muted hover:bg-bg-hover hover:text-fg text-xs"
              >
                <RotateCw className="h-3.5 w-3.5" />
                retry
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={9} className="px-5 py-3 bg-bg-inset/40">
            {job.error && (
              <p className="text-danger font-mono text-xs mb-3">{job.error}</p>
            )}

            <LiveWorkers jobID={job.id} />

            <p className="orb-section-title text-fg-muted mb-2 mt-4">
              Per-repo progress ({detail?.repos?.length ?? 0})
            </p>
            {detail?.repos?.length ? (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-fg-muted">
                    <th className="py-1">Status</th>
                    <th className="py-1">Source key</th>
                    <th className="py-1">Format / Kind</th>
                    <th className="py-1">Artifacts</th>
                    <th className="py-1">Bytes</th>
                    <th className="py-1">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.repos.map((r) => (
                    <tr key={r.src_key} className="border-t border-border/40">
                      <td className="py-1.5">
                        <Badge tone={repoStatusTone(r.status)}>
                          {r.status}
                        </Badge>
                      </td>
                      <td className="py-1.5 font-mono text-fg">{r.src_key}</td>
                      <td className="py-1.5 text-fg-muted">
                        {r.src_format} / {r.src_kind}
                      </td>
                      <td className="py-1.5 text-fg-muted">{r.artifacts_done}/{r.artifacts_total}</td>
                      <td className="py-1.5 text-fg-muted">{fmtBytes(r.bytes_done)}</td>
                      <td className="py-1.5 text-fg-muted truncate max-w-md">{r.error || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-fg-subtle text-xs">No per-repo rows yet.</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function LiveWorkers({ jobID }: Readonly<{ jobID: string }>) {
  const { data: workers } = useImportWorkers(jobID);
  if (!workers || workers.length === 0) {
    return (
      <p className="text-fg-subtle text-xs italic">
        No live workers — job is idle, transferring on another node, or finished.
      </p>
    );
  }
  return (
    <div>
      <p className="orb-section-title text-fg-muted mb-2">
        Live workers ({workers.length}) — heartbeat every 5s
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {workers.map((w) => {
          const pct = w.current_size > 0 ? (w.bytes_so_far / w.current_size) * 100 : 0;
          return (
            <div
              key={w.worker_id}
              className={'rounded-md border p-3 ' + workerCardClassName(w.stale, w.current_artifact_id)}
            >
              <p className="text-[10px] text-fg-subtle font-mono truncate">{w.worker_id}</p>
              {w.current_artifact_id > 0 ? (
                <>
                  <p className="text-xs text-fg font-mono break-all mt-1" title={w.current_path}>
                    {w.current_path}
                  </p>
                  <p className="text-[10px] text-fg-muted mt-0.5">
                    {fmtBytes(w.bytes_so_far)} / {fmtBytes(w.current_size)} · {pct.toFixed(0)}%
                  </p>
                  <div className="h-1 mt-1 bg-bg-inset rounded">
                    <div className="h-1 rounded bg-accent" style={{ width: pct + '%' }} />
                  </div>
                </>
              ) : (
                <p className="text-xs text-fg-subtle italic mt-1">
                  {w.stale ? 'stale (no heartbeat)' : 'idle (waiting for queue)'}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
